import { useEffect, useMemo, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { Refresh } from '@mui/icons-material'
import { PageShell } from '@components/common/PageShell'
import type { NdaVersionDto } from '@api/types/nda'
import {
  useNdaAcceptances,
  useNdaCurrent,
  useNdaVersions,
  useSaveNda,
  useUserRoster,
} from '../hooks/useNda'
import {
  NdaAcceptanceRecord,
  NdaEditor,
  NdaVersionHistory,
  PublishNdaDialog,
  shortHash,
} from '../components/nda'

/**
 * The non-disclosure agreement that gates the app.
 *
 * The screen is built around the one thing about this feature that is easy to get wrong by
 * accident: saving changed text is not saving a document, it is revoking everybody's access
 * until they accept the new words. So the draft is labelled as the *next* version throughout,
 * the consequence is stated while you type rather than after you save, and published versions
 * are shown as a sealed record you can read but never edit.
 */
export default function AdminNdaPage() {
  const currentQuery = useNdaCurrent()
  const versionsQuery = useNdaVersions()
  const save = useSaveNda()

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  // Which published text the draft was seeded from, so background refetches don't wipe edits
  // in progress but an actual publish does re-seed.
  const seededFrom = useRef<string | null>(null)

  // null means "follow whatever is in force" — the useful default, and it avoids a second
  // source of truth that has to be kept in step with the current version.
  const [versionChoice, setVersionChoice] = useState<number | 'all' | null>(null)

  const [publishOpen, setPublishOpen] = useState(false)
  const [rosterWanted, setRosterWanted] = useState(false)

  const current = currentQuery.data
  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data])
  const currentVersion = current?.published ? (current.version ?? null) : null

  const selectedVersion: number | 'all' = versionChoice ?? currentVersion ?? 'all'

  const acceptancesQuery = useNdaAcceptances(
    selectedVersion === 'all' ? undefined : selectedVersion,
    // Held until the current version is known, so the first read is the one we want.
    !!current,
  )
  const rosterQuery = useUserRoster(rosterWanted)

  // Seed the draft from the published text on load, and again whenever the published text
  // changes underneath us (our own publish, or somebody else's).
  useEffect(() => {
    if (!current) return
    const key = current.published ? (current.contentHash ?? '') : 'unpublished'
    if (seededFrom.current === key) return
    seededFrom.current = key
    setTitle(current.title ?? '')
    setBody(current.body ?? '')
  }, [current])

  const acceptedCurrentCount = useMemo(
    () => versions.find((v) => v.isCurrent)?.acceptedCount ?? 0,
    [versions],
  )

  const handleUseAsDraft = (version: NdaVersionDto) => {
    setTitle(version.title)
    setBody(version.body)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDiscard = () => {
    setTitle(current?.title ?? '')
    setBody(current?.body ?? '')
    save.reset()
  }

  const handlePublish = () => {
    save.mutate(
      { title, body },
      {
        // Closed either way: on success the new current payload re-seeds the draft, and on
        // failure the error belongs on the editor behind this dialog, where it is readable.
        onSettled: () => setPublishOpen(false),
      },
    )
  }

  const handleRefresh = () => {
    currentQuery.refetch()
    versionsQuery.refetch()
    acceptancesQuery.refetch()
    if (rosterWanted) rosterQuery.refetch()
  }

  // The early returns keep <PageShell> so the AppBar title is right while the page is still
  // loading or has failed — PageShell is what sets it.
  if (currentQuery.isLoading) {
    return (
      <PageShell title="Non-disclosure agreement">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    )
  }

  if (currentQuery.error || !current) {
    return (
      <PageShell title="Non-disclosure agreement">
        <Alert severity="error">
          Failed to load the agreement. {(currentQuery.error as Error)?.message}
        </Alert>
      </PageShell>
    )
  }

  const saveError = save.error as AxiosError<{ message?: string }> | null
  const serverError = saveError?.response?.data?.message ?? (saveError ? 'Failed to publish.' : null)

  return (
    <PageShell
      title="Non-disclosure agreement"
      subtitle="The agreement every user must accept before they can use the app."
      actions={
        <Button startIcon={<Refresh />} onClick={handleRefresh}>
          Refresh
        </Button>
      }
    >
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Publishing is not saving a document</AlertTitle>
        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
          <li>
            <strong>Changed text mints a new version, and every user is then held at an
            acceptance screen until they accept it</strong> — they cannot use the app at all in the
            meantime. Nobody is notified; they meet it next time they open the app.
          </li>
          <li>
            <strong>Unchanged text does nothing.</strong> The server hashes the text with line
            endings and surrounding blank space ignored, so re-saving the same words — or changing
            something and changing it back — mints nothing and asks nobody to sign again.
          </li>
          <li>
            <strong>Published versions are immutable.</strong> You cannot correct version{' '}
            {currentVersion ?? 'n'}; you publish version {(currentVersion ?? 0) + 1}. The old text
            and its signatures stay on the record, because each signature has to point at the exact
            words that were agreed to.
          </li>
        </ul>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <NdaEditor
            current={current}
            title={title}
            body={body}
            onTitleChange={setTitle}
            onBodyChange={setBody}
            onRequestPublish={() => setPublishOpen(true)}
            onDiscard={handleDiscard}
            isSaving={save.isPending}
            acceptedCurrentCount={acceptedCurrentCount}
            lastResult={save.data ?? null}
            serverError={serverError}
          />
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Gate status
            </Typography>

            {current.published ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  In force
                </Typography>
                <Typography variant="h5" gutterBottom>
                  Version {current.version}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Published
                    </Typography>
                    <Typography variant="body2">
                      {new Date(current.createdAt ?? '').toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Text hash
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {shortHash(current.contentHash)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Signatures on this version
                    </Typography>
                    <Typography variant="body2">
                      {acceptedCurrentCount}{' '}
                      {acceptedCurrentCount === 1 ? 'person has' : 'people have'} accepted it
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Versions published
                    </Typography>
                    <Typography variant="body2">{versions.length}</Typography>
                  </Box>
                </Stack>

                <Alert severity="info" sx={{ mt: 2 }}>
                  Everyone who has not accepted version {current.version} is blocked from the app
                  right now — the &ldquo;Not yet accepted&rdquo; tab below names them.
                </Alert>
              </>
            ) : (
              <Alert severity="warning">
                <AlertTitle>No agreement published</AlertTitle>
                The gate treats &ldquo;no document&rdquo; as nothing to accept, so the app is open
                to everybody. Publishing version 1 turns it on.
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <NdaVersionHistory
            versions={versions}
            isLoading={versionsQuery.isLoading}
            error={versionsQuery.error}
            onUseAsDraft={handleUseAsDraft}
            onShowAcceptances={setVersionChoice}
          />
        </Grid>

        <Grid item xs={12}>
          <NdaAcceptanceRecord
            versions={versions}
            selectedVersion={selectedVersion}
            onSelectedVersionChange={setVersionChoice}
            acceptances={acceptancesQuery.data ?? []}
            isLoading={acceptancesQuery.isLoading}
            error={acceptancesQuery.error}
            currentVersion={currentVersion}
            roster={rosterQuery.data}
            rosterLoading={rosterQuery.isLoading && rosterWanted}
            rosterError={rosterQuery.error}
            onNeedRoster={() => setRosterWanted(true)}
          />
        </Grid>
      </Grid>

      <PublishNdaDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        onConfirm={handlePublish}
        isSaving={save.isPending}
        title={title}
        body={body}
        currentVersion={currentVersion ?? 0}
        nextVersion={(currentVersion ?? 0) + 1}
        acceptedCurrentCount={acceptedCurrentCount}
      />
    </PageShell>
  )
}
